import React from 'react';
import styled from 'styled-components';
import BuilderExecutionDateRange from '../../../../../components/Builder/BuilderExecutionDateRange';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  builderSelectedOptionsSelector,
  selectTimeUnits,
} from '../../../../../redux/selectors/builder';
import { assertCondition } from '../../../../../utils/types';
import {
  DateKindKey,
  ExpressionModeKey,
  QueryType,
  TimeUnit,
} from '../../../../../utils/apollo/graphqlSchema';
import ComparisonConfigPicker from './ComparisonConfigPicker';
import {
  resetSegments,
  RowType,
  resetBreakdownGroups,
  setTimeUnit,
} from '../../../../../redux/slices/oldBuilder';
import QueryDateKind from './QueryDateKind';
import QueryTimeWindow from './QueryTimeWindow';
import TimeUnitMenu from '../../../../../components/Builder/BuilderFields/TimeUnitMenu';
import MissingDateControl from './MissingDateControl';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 16px;
  row-gap: 8px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

const QueryDateRangeSettings = () => {
  const dispatch = useAppDispatch();

  const selectedOptions = useAppSelector((state) =>
    builderSelectedOptionsSelector(state),
  );
  const breakdownGroups = useAppSelector(
    ({ builder }) => builder.breakdown.groups,
  );
  const segments = useAppSelector(
    ({ builder }) => builder[RowType.breakdownCohorts],
  );
  const timeUnits = useAppSelector(selectTimeUnits);

  assertCondition(selectedOptions?.type === QueryType.Expression);

  const {
    mode: selectedQueryMode,
    timeUnit,
    dateKind,
    includeMissingDates,
  } = selectedOptions;

  const handleChangeTimeUnit = (newTimeUnit: TimeUnit) => {
    dispatch(setTimeUnit({ newTimeUnit }));

    // Because Breakdown is available only for ALL_TIME time unit in
    // Histogram mode, we have to manually reset active groups and segments
    if (
      selectedQueryMode === ExpressionModeKey.Histogram &&
      newTimeUnit !== TimeUnit.AllTime
    ) {
      if (breakdownGroups.length > 0) {
        dispatch(resetBreakdownGroups());
      }
      if (segments.length > 0) {
        dispatch(resetSegments());
      }
    }
  };

  const showDateKind =
    selectedQueryMode === ExpressionModeKey.Histogram ||
    selectedQueryMode === ExpressionModeKey.Query;

  return (
    <Container>
      <BuilderExecutionDateRange />
      <TimeUnitMenu
        value={timeUnit}
        timeUnits={timeUnits}
        onChange={handleChangeTimeUnit}
      />
      {/* comparison and missing dates are mutually exclusive */}
      {selectedQueryMode === ExpressionModeKey.Query && (
        <ComparisonConfigPicker />
      )}
      {showDateKind && (
        <Row>
          <QueryDateKind />
          {dateKind === DateKindKey.Event && (
            <MissingDateControl includeMissingDates={includeMissingDates} />
          )}
          {dateKind === DateKindKey.Install && <QueryTimeWindow />}
        </Row>
      )}
    </Container>
  );
};

export default QueryDateRangeSettings;
